import { orderedQuestions, orderedSections } from "./logic";
import { definitionFor } from "./registry";
import type { FormContent, ValidationRule } from "./types";

// Rule kinds are map keys in the collaborative document, not authored order.
function validationByKind(rules: ValidationRule[] | undefined) {
  return rules
    ?.slice()
    .sort((a, b) => (a.kind < b.kind ? -1 : a.kind > b.kind ? 1 : 0));
}

export type FormChange = {
  id: string;
  kind: "added" | "removed" | "changed";
  label: string;
  sectionId?: string;
  questionId?: string;
  fields: string[];
};

// Object insertion order and omitted optional values aren't authored changes.
function canonical(value: unknown): string {
  if (value === undefined) {
    return "undefined";
  }

  if (Array.isArray(value)) {
    return `[${value.map(canonical).join(",")}]`;
  }

  if (value && typeof value === "object") {
    return `{${Object.entries(value)
      .filter(([, v]) => v !== undefined)
      .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))
      .map(([k, v]) => `${JSON.stringify(k)}:${canonical(v)}`)
      .join(",")}}`;
  }

  return JSON.stringify(value);
}

/** Only report items whose own position changed relative to surviving siblings. */
function movedIds(
  before: { id: string; pos: string }[],
  after: { id: string; pos: string }[],
): Set<string> {
  const oldById = new Map(before.map((item) => [item.id, item]));
  const nextIds = new Set(after.map((item) => item.id));
  const oldOrder = before.filter((item) => nextIds.has(item.id));
  const newOrder = after.filter((item) => oldById.has(item.id));
  return new Set(
    newOrder
      .filter((item, index) => {
        const changedRelativeOrder = oldOrder[index]?.id !== item.id;
        const changedOwnPosition = oldById.get(item.id)?.pos !== item.pos;
        return changedRelativeOrder && changedOwnPosition;
      })
      .map((item) => item.id),
  );
}

/** Includes all draft data, including orphaned questions, for publication fencing. */
export function formContentFingerprint(content: FormContent): string {
  return canonical({
    title: content.title,
    description: content.description,
    groups: content.groups?.slice().sort((a, b) => a.id.localeCompare(b.id)),
    sections: orderedSections(content),
    questions: [...content.questions]
      .sort((a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0))
      .map((question) => ({
        ...question,
        validation: validationByKind(question.validation),
      })),
  });
}

/** One change per authored entity, comparing identity rather than array placement. */
export function describeFormChanges(
  before: FormContent | null,
  after: FormContent,
): FormChange[] {
  const changes: FormChange[] = [];
  const movedSections = movedIds(
    before ? orderedSections(before) : [],
    orderedSections(after),
  );
  const movedQuestions = new Set<string>();

  for (const question of after.questions) {
    const previous = before?.questions.find((q) => q.id === question.id);

    if (previous && previous.sectionId !== question.sectionId) {
      movedQuestions.add(question.id);
    }
  }

  for (const section of after.sections) {
    for (const id of movedIds(
      before ? orderedQuestions(before, section.id) : [],
      orderedQuestions(after, section.id),
    )) {
      movedQuestions.add(id);
    }
  }

  const compare = (
    old: Record<string, unknown>,
    next: Record<string, unknown>,
  ) =>
    Object.keys(next).filter(
      (key) => canonical(old[key]) !== canonical(next[key]),
    );
  const formFields = compare(
    before
      ? { Title: before.title, Description: before.description || undefined }
      : {},
    { Title: after.title, Description: after.description || undefined },
  );

  if (formFields.length) {
    changes.push({
      id: "form",
      label: after.title || "Untitled form",
      kind: before ? "changed" : "added",
      fields: formFields,
    });
  }

  const groupFields = (form: FormContent) =>
    (form.groups ?? []).map((group) => ({
      id: group.id,
      label: group.title || "Untitled group",
      sectionId: group.sectionId,
      fields: {
        Title: group.title,
        Description: group.description,
        Repeat: group.repeat,
      },
    }));
  const sectionFields = (form: FormContent) =>
    orderedSections(form).map((s) => ({
      id: s.id,
      label: s.title || "Untitled section",
      sectionId: s.id,
      fields: {
        Title: s.title,
        Description: s.description || undefined,
        Visibility: s.visibleWhen,
        Routes: s.jumps,
        "Next section": s.defaultNextSectionId,
      },
    }));
  const questionFields = (form: FormContent) =>
    form.questions.map((q) => ({
      id: q.id,
      label: q.label || "Untitled question",
      sectionId: q.sectionId,
      questionId: q.id,
      fields: {
        Question: q.label,
        "Short description": q.shortDescription || undefined,
        "Help text": q.help || undefined,
        Type: definitionFor(q.type).label,
        Required: q.required,
        Settings: q.config && {
          ...q.config,
          options: q.config.options?.map(({ pos: _pos, ...option }) => option),
          rows: q.config.rows?.map(({ pos: _pos, ...row }) => row),
        },
        Validation: validationByKind(q.validation),
        Visibility: q.visibleWhen,
        Section: q.sectionId,
        Group: q.groupId,
      },
    }));

  const entityComparisons = [
    {
      entries: sectionFields,
      moved: movedSections,
      positionLabel: "Section position",
    },
    {
      entries: questionFields,
      moved: movedQuestions,
      positionLabel: "Position in section",
    },
    {
      entries: groupFields,
      moved: movedQuestions,
      positionLabel: "Position in section",
    },
  ];

  for (const { entries, moved, positionLabel } of entityComparisons) {
    const old = before ? entries(before) : [];
    const next = entries(after);
    const previous = new Map(old.map((entry) => [entry.id, entry] as const));

    for (const entry of next) {
      const source = previous.get(entry.id);
      const fields = source ? compare(source.fields, entry.fields) : [];

      if (source && moved.has(entry.id)) {
        fields.push(positionLabel);
      }

      if (!source || fields.length) {
        changes.push({ ...entry, fields, kind: source ? "changed" : "added" });
      }
    }

    for (const entry of old) {
      if (!next.some((item) => item.id === entry.id)) {
        changes.push({ ...entry, fields: [], kind: "removed" });
      }
    }
  }

  return changes;
}
